/**
 * Settings → Connect tab (/settings/connect) — guides the user through
 * connecting their AI coding assistant to the SystemDox MCP server
 * (mirrors the public guide at https://www.systemdox.com/guides/mcp-setup).
 * The standards the server serves are surfaced separately on the Docs home
 * (/docs, "What your AI reads").
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  Copy,
  ExternalLink,
  Key,
  MessageSquareText,
  Plug,
  RefreshCw,
  Terminal,
} from 'lucide-react';

const SETUP_COMMAND = 'npx @systemdox/mcp-server setup';

const MCP_JSON = `{
  "mcpServers": {
    "systemdox": {
      "command": "npx",
      "args": ["@systemdox/mcp-server"],
      "env": {
        "SYSTEMDOX_API_KEY": "your-api-key-here",
        "SYSTEMDOX_API_URL": "https://api.puglieseweb.com/systemdox"
      }
    }
  }
}`;

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — the user can still select the text manually.
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title="Copy to clipboard"
      className="shrink-0 rounded-md p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700/60 transition-colors"
    >
      {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
    </button>
  );
}

function StepBadge({ n }: { n: number }) {
  return (
    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm shrink-0">
      {n}
    </div>
  );
}

export default function BuildConnectPage() {
  return (
    <div className="space-y-8">
      {/* Why connect */}
      <div className="flex items-start gap-4 rounded-xl border border-blue-200 dark:border-blue-900 bg-blue-50/50 dark:bg-blue-950/30 p-6">
        <div className="p-2.5 bg-blue-100 dark:bg-blue-900/40 rounded-xl shrink-0">
          <Plug className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-1">
            Connect your AI to SystemDox
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            The SystemDox MCP server gives Claude Code, Cursor, or any MCP-compatible tool
            direct access to your checks, project rules, specs, and documentation —
            so it writes code that follows your standards from the start, without
            copy-pasting rules into prompts.
          </p>
          <a
            href="https://www.systemdox.com/guides/mcp-setup"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            Read the full setup guide
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* Setup steps */}
      <div className="space-y-4">
        {/* Step 1 */}
        <div className="flex items-start gap-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <StepBadge n={1} />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2 mb-1">
              <Key className="w-4 h-4 text-amber-500" />
              Get your API key
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Generate a SystemDox API key in{' '}
              <Link to="/settings/api-keys" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
                Settings → API Keys
              </Link>{' '}
              — the MCP server uses it to authenticate with the SystemDox API. It is never
              sent to the AI model itself.
            </p>
          </div>
        </div>

        {/* Step 2 */}
        <div className="flex items-start gap-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <StepBadge n={2} />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2 mb-1">
              <Terminal className="w-4 h-4 text-emerald-500" />
              Run the setup wizard
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
              The interactive setup detects your installed AI tools (Claude Code, Cursor,
              VS Code), validates your API key, and writes the configuration for you.
              Choose <strong>user scope</strong> to enable SystemDox across all your
              projects, or <strong>project scope</strong> for a single repo.
            </p>
            <div className="flex items-center gap-2 bg-slate-900 rounded-lg px-4 py-3 font-mono text-sm text-slate-200 overflow-x-auto">
              <code className="flex-1 whitespace-nowrap">
                <span className="text-blue-400">npx</span> @systemdox/mcp-server{' '}
                <span className="text-emerald-400">setup</span>
              </code>
              <CopyButton text={SETUP_COMMAND} />
            </div>
            <details className="mt-3 group">
              <summary className="text-sm text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700 dark:hover:text-slate-300">
                Prefer manual configuration? Show the <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">.mcp.json</code> snippet
              </summary>
              <div className="mt-2">
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                  Add to <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">.mcp.json</code> in
                  your project root (Claude Code), <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">.cursor/mcp.json</code> (Cursor),
                  or <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">~/.claude/.mcp.json</code> for
                  all projects:
                </p>
                <div className="flex items-start gap-2 bg-slate-900 rounded-lg px-4 py-3 font-mono text-xs text-slate-200 overflow-x-auto">
                  <pre className="flex-1 whitespace-pre">{MCP_JSON}</pre>
                  <CopyButton text={MCP_JSON} />
                </div>
              </div>
            </details>
          </div>
        </div>

        {/* Step 3 */}
        <div className="flex items-start gap-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <StepBadge n={3} />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2 mb-1">
              <RefreshCw className="w-4 h-4 text-blue-500" />
              Restart and verify
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
              Restart your AI tool, then confirm the connection by asking:
            </p>
            <div className="flex items-start gap-2 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 px-4 py-3">
              <MessageSquareText className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
              <p className="text-sm font-mono text-slate-700 dark:text-slate-300">
                "Use the get_context tool to show me my SystemDox standards."
              </p>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-3">
              If configured correctly, the assistant returns your tech stack, checks,
              project rules, and relevant specs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
